import React, { useEffect } from 'react';
import { logger } from './utils/logger';
import { HashRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import AppRoutes from './routes/AppRoutes';
import frLocale from 'date-fns/locale/fr';

// Création du thème personnalisé
const theme = createTheme({
  palette: {
    primary: {
      main: '#0f4c5c',
      light: '#1a6b80',
      dark: '#093340',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#1a1a1a',
    },
    background: {
      default: '#f0f4f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2332',
      secondary: '#4a5568',
    },
    success: { main: '#10b981' },
    warning: { main: '#f59e0b' },
    error: { main: '#ef4444' },
    info: { main: '#3b82f6' },
  },
  typography: {
    fontFamily: ['"DM Sans"', 'Outfit', 'Arial', 'sans-serif'].join(','),
    h1: { fontFamily: '"Outfit", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Outfit", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Outfit", sans-serif', fontWeight: 600 },
    h4: { fontFamily: '"Outfit", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Outfit", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Outfit", sans-serif', fontWeight: 600 },
    button: { fontFamily: '"DM Sans", sans-serif', fontWeight: 500 },
  },
  shape: { borderRadius: 10 },
  shadows: [
    'none',
    '0 1px 3px rgba(15,76,92,0.08), 0 1px 2px rgba(15,76,92,0.06)',
    '0 4px 6px rgba(15,76,92,0.07), 0 2px 4px rgba(15,76,92,0.06)',
    '0 10px 15px rgba(15,76,92,0.08), 0 4px 6px rgba(15,76,92,0.05)',
    '0 20px 25px rgba(15,76,92,0.09), 0 10px 10px rgba(15,76,92,0.04)',
    '0 25px 50px rgba(15,76,92,0.12)',
    ...Array(19).fill('none'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          letterSpacing: '0.01em',
          borderRadius: 8,
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #0f4c5c 0%, #1a6b80 100%)',
          boxShadow: '0 4px 14px rgba(15,76,92,0.35)',
          '&:hover': {
            background: 'linear-gradient(135deg, #093340 0%, #0f4c5c 100%)',
            boxShadow: '0 6px 20px rgba(15,76,92,0.45)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(15,76,92,0.07), 0 1px 3px rgba(15,76,92,0.06)',
          border: '1px solid rgba(15,76,92,0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 4px 6px rgba(15,76,92,0.07), 0 1px 3px rgba(15,76,92,0.06)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover fieldset': { borderColor: '#0f4c5c' },
            '&.Mui-focused fieldset': { borderColor: '#0f4c5c', borderWidth: 2 },
          },
          '& label.Mui-focused': { color: '#0f4c5c' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500 },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #0f4c5c 0%, #1a6b80 100%)',
          boxShadow: '0 2px 20px rgba(15,76,92,0.25)',
        },
      },
    },
  },
});

function App() {
  useEffect(() => {
    logger.info('Application démarrée', {
      environment: process.env.NODE_ENV,
      version: process.env.REACT_APP_VERSION || '1.0.0'
    });
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <HashRouter>
            <AppRoutes />
          </HashRouter>
        </AuthProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App;
